import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const CommunityIdentityDialog = lazy(
  () => import('@/components/auth/CommunityIdentityDialog.jsx'),
);

const CommunityIdentityContext = createContext(null);

export function CommunityIdentityProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const pendingRequestRef = useRef(null);

  const settleRequest = useCallback((result) => {
    const pendingRequest = pendingRequestRef.current;
    pendingRequestRef.current = null;
    setIsOpen(false);
    pendingRequest?.resolve(result);
  }, []);

  const requestCommunityIdentity = useCallback(() => {
    if (pendingRequestRef.current) {
      return pendingRequestRef.current.promise;
    }

    let resolveRequest;
    const promise = new Promise((resolve) => {
      resolveRequest = resolve;
    });
    pendingRequestRef.current = {
      promise,
      resolve: resolveRequest,
    };
    setIsOpen(true);
    return promise;
  }, []);

  const closeCommunityIdentity = useCallback(() => {
    settleRequest({ completed: false, communityIdentity: null });
  }, [settleRequest]);

  const completeCommunityIdentityRequest = useCallback((communityIdentity) => {
    settleRequest({ completed: true, communityIdentity });
  }, [settleRequest]);

  useEffect(() => () => {
    const pendingRequest = pendingRequestRef.current;
    pendingRequestRef.current = null;
    pendingRequest?.resolve({ completed: false, communityIdentity: null });
  }, []);

  const value = useMemo(() => ({
    isCommunityIdentityOpen: isOpen,
    requestCommunityIdentity,
    closeCommunityIdentity,
  }), [closeCommunityIdentity, isOpen, requestCommunityIdentity]);

  return (
    <CommunityIdentityContext.Provider value={value}>
      {children}
      {isOpen ? (
        <Suspense fallback={null}>
          <CommunityIdentityDialog
            open
            onClose={closeCommunityIdentity}
            onCompleted={completeCommunityIdentityRequest}
          />
        </Suspense>
      ) : null}
    </CommunityIdentityContext.Provider>
  );
}

export const useCommunityIdentity = () => {
  const context = useContext(CommunityIdentityContext);
  if (!context) {
    throw new Error(
      'useCommunityIdentity must be used within a CommunityIdentityProvider',
    );
  }
  return context;
};
